import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../app";

interface TypeState {
  type: string;
  loadurl: string;
  chaturl: string;
  geturl: string;
  showSecondary: boolean;
  list_of_files: any[];
  prompt_storage: string;
  prompts: string[];
  uploadtypes: string[];
  deleteurl: string;
  loaddb: string;
  userFormats: string[];

  // Multiple selected prompts
  userPrompt: string[];

  datapreviewurl: string;
  shortname: string;
  shared_files: string[];
  mode: string[];
}

const initialState: TypeState = {
  type: "",
  loadurl: "",
  chaturl: "",
  geturl: "",
  showSecondary: false,
  list_of_files: [],
  prompt_storage: "",
  prompts: [],
  uploadtypes: [],
  userFormats: [],
  deleteurl: "",
  loaddb: "",
  userPrompt: [],
  datapreviewurl: "",
  shortname: "",
  shared_files: [],
  mode: ["chat"],
};

export const typeSlice = createSlice({
  name: "type",
  initialState,

  reducers: {
    addType: (state, action) => {
      state.chaturl = action.payload.chaturl;
      state.loadurl = action.payload.loadurl;
      state.type = action.payload.name;
      state.geturl = action.payload.geturl;
      state.showSecondary = action.payload.showSecondary;
      state.prompt_storage = action.payload.prompt_storage;
      state.uploadtypes = action.payload.uploadtypes;
      state.deleteurl = action.payload.deleteurl;
      state.loaddb = action.payload.loaddb;

      // Clear selected prompts when the chat type changes
      state.userPrompt = [];

      state.datapreviewurl = action.payload.datapreviewurl;
      state.shortname = action.payload.shortname;
      state.mode = action.payload.mode;
    },

    addFiles: (state, action: PayloadAction<any[]>) => {
      state.showSecondary = true;
      state.list_of_files = action.payload;
    },

    addPrompts: (state, action: PayloadAction<string[]>) => {
      state.showSecondary = true;
      state.prompts = action.payload;
    },

    addUserFormats: (state, action: PayloadAction<string[]>) => {
      state.showSecondary = true;
      state.userFormats = action.payload;
    },

    // Select or deselect one prompt
    addUserFormatPrompt: (state, action: PayloadAction<string>) => {
      const prompt = action.payload;

      if (!prompt) {
        return;
      }

      const isAlreadySelected = state.userPrompt.includes(prompt);

      if (isAlreadySelected) {
        state.userPrompt = state.userPrompt.filter(
          (selectedPrompt) => selectedPrompt !== prompt,
        );
      } else {
        state.userPrompt.push(prompt);
      }
    },

    // Remove one prompt after deleting it
    removeUserFormatPrompt: (state, action: PayloadAction<string>) => {
      state.userPrompt = state.userPrompt.filter(
        (selectedPrompt) => selectedPrompt !== action.payload,
      );
    },

    // Update a selected prompt after editing it
    replaceUserFormatPrompt: (
      state,
      action: PayloadAction<{
        oldPrompt: string;
        newPrompt: string;
      }>,
    ) => {
      const { oldPrompt, newPrompt } = action.payload;

      state.userPrompt = state.userPrompt.map((selectedPrompt) =>
        selectedPrompt === oldPrompt ? newPrompt : selectedPrompt,
      );
    },

    // Clear all selected prompts
    clearUserFormatPrompts: (state) => {
      state.userPrompt = [];
    },

    removeType: () => {
      return {
        ...initialState,
      };
    },

    addSharedFiles: (state, action: PayloadAction<string[]>) => {
      state.shared_files = action.payload;
    },
  },
});

export const {
  addType,
  addFiles,
  addPrompts,
  addUserFormats,
  addUserFormatPrompt,
  removeUserFormatPrompt,
  replaceUserFormatPrompt,
  clearUserFormatPrompts,
  removeType,
  addSharedFiles,
} = typeSlice.actions;

export const getType = (state: RootState) => state.type;

export const getShowSecondary = (state: RootState) => state.type.showSecondary;

export const getFiles = (state: RootState) => state.type.list_of_files;

export const getPrompts = (state: RootState) => state.type.prompts;

export const getUserFormats = (state: RootState) => state.type.userFormats;

// This now returns string[]
export const getUserFormatPrompt = (state: RootState): string[] =>
  state.type.userPrompt;

export const getSharedDocuments = (state: RootState) => state.type.shared_files;

export default typeSlice.reducer;
